import { useEffect } from 'react';
import { initializeSdk } from '@eventbrite/legacy-infra.statsig';

import { getOptions, getUser, shouldInitStatsig } from './utils';
import type { StatsigListingsConfig } from './types';

let initialized = false;
const initCallbacks: Array<() => void> = [];

/**
 * Reset module state - FOR TESTING ONLY
 * @internal
 */
export const __resetForTesting = (): void => {
  initialized = false;
  initCallbacks.length = 0;
};

/**
 * Register a callback to run when Statsig is initialized.
 * If already initialized, runs immediately.
 */
export const onStatsigReady = (callback: () => void): void => {
  if (initialized) {
    callback();
  } else {
    initCallbacks.push(callback);
  }
};

/**
 * Initialize Statsig client
 */
export const useInitStatsig = (config: StatsigListingsConfig): void => {
  const { clientKey, stableId, custom, environmentTier } = config;

  useEffect(() => {
    if (initialized || !shouldInitStatsig(config)) {
      return;
    }

    const initialize = async () => {
      try {
        const statsigUser = getUser(stableId, custom);
        const statsigOptions = getOptions(stableId, environmentTier);

        await initializeSdk({
          clientKey,
          user: statsigUser,
          options: statsigOptions,
        });
        initialized = true;

        while (initCallbacks.length > 0) {
          const callback = initCallbacks.shift();
          if (callback) callback();
        }
      } catch (error) {
        console.error('Statsig client initialization failed', {
          error,
          stableId: stableId || 'missing',
          environmentTier: environmentTier || 'missing',
        });
      }
    };

    initialize();
  }, [clientKey, stableId, custom, environmentTier, config]);
};
